import {
  ACCOUNTING_PAYMENT_METHODS,
  EXPENSE_PAYMENT_SOURCES,
  type FundsAccountKind,
  type FundsAutopostSource,
} from './accounting';
import type { EcomPaymentMethodsConfig } from './ecom-payment-methods';
import type { PayoutDeductionsConfig } from './payout-deductions';
import type { PosPaymentMethodsConfig } from './pos-payment-methods';

/**
 * Picking a sensible default funds account for a payment method.
 *
 * Two payment vocabularies exist and they DISAGREE: supplier payments and
 * consignment receipts use `ACCOUNTING_PAYMENT_METHODS` ('bank_transfer'),
 * expenses use `EXPENSE_PAYMENT_SOURCES` ('bank'). Rather than alias one onto
 * the other, this maps the UNION explicitly — an exhaustiveness test covers
 * every member of both lists, so adding a method to either one fails the test
 * instead of silently falling through to a wrong default.
 *
 * Pure: the admin and cashier pickers share it so their defaults can't drift.
 */
export type FundsAccountPick = {
  id: string;
  kind: FundsAccountKind;
  branchId: string | null;
  active?: boolean;
};

/** Preferred account kinds per payment method, most-specific first. */
const PREFERRED: Record<string, readonly FundsAccountKind[]> = {
  // ACCOUNTING_PAYMENT_METHODS
  cash: ['cash'],
  bank_transfer: ['bank'],
  gcash: ['ewallet', 'bank'],
  check: ['cheque', 'bank'],
  other: ['bank', 'cash'],
  // EXPENSE_PAYMENT_SOURCES adds this one spelling
  bank: ['bank'],
};

const FALLBACK: readonly FundsAccountKind[] = ['bank', 'cash'];

/** Every method string the two vocabularies can produce (for the test). */
export const FUNDS_METHOD_KEYS = [
  ...new Set<string>([...ACCOUNTING_PAYMENT_METHODS, ...EXPENSE_PAYMENT_SOURCES]),
];

export function preferredFundsKinds(method: string): readonly FundsAccountKind[] {
  return PREFERRED[method] ?? FALLBACK;
}

/**
 * The account a picker should preselect, or null when nothing fits.
 *
 * Cash is branch-bound (a till belongs to a branch), so a cash payment prefers
 * the till of the branch it is being recorded at, then a branchless cash
 * account, then any cash account. Non-cash kinds are company-wide and are
 * matched by preference order alone.
 */
export function defaultFundsAccountFor(
  accounts: FundsAccountPick[],
  opts: { method: string; branchId: string | null },
): string | null {
  const usable = accounts.filter((a) => a.active !== false);
  if (usable.length === 0) return null;

  for (const kind of preferredFundsKinds(opts.method)) {
    const ofKind = usable.filter((a) => a.kind === kind);
    if (ofKind.length === 0) continue;
    if (kind === 'cash') {
      const atBranch = opts.branchId ? ofKind.find((a) => a.branchId === opts.branchId) : undefined;
      const branchless = ofKind.find((a) => a.branchId === null);
      const pick = atBranch ?? branchless ?? ofKind[0];
      if (pick) return pick.id;
      continue;
    }
    const pick = ofKind[0];
    if (pick) return pick.id;
  }
  return null;
}

// ── Auto-posting rule editor: code suggestions ─────────────────────────────

export type FundsCodeSuggestion = { code: string; label: string };

/** The registries a rule's `code` can come from — a subset of TenantFeatures. */
export type FundsSuggestionFeatures = {
  pos_payment_methods: PosPaymentMethodsConfig;
  ecom_payment_methods: EcomPaymentMethodsConfig;
  payout_deductions: PayoutDeductionsConfig;
  merchant_payout_deductions: PayoutDeductionsConfig;
  creator_payout_deductions: PayoutDeductionsConfig;
};

/**
 * Method free-texted onto an admin-recorded offline payment when the order
 * names no method (`POST /admin/orders/:id/record-offline-payment`). Listed so
 * a rule can catch it explicitly rather than only through a wildcard.
 */
export const OFFLINE_PAYMENT_METHOD_CODE = 'Offline payment';

const methodList = <T extends { code: string; label: string }>(
  cfg: { methods?: T[] } | null | undefined,
) => (Array.isArray(cfg?.methods) ? cfg.methods : []);

/**
 * Which registry codes each auto-post source can carry, so the rule editor
 * can suggest them. Pure and total over `FundsAutopostSource` — a new source
 * without a suggestion list is a compile error, not an empty dropdown. Codes
 * stay free text on the rule itself: a retired method's history still needs a
 * rule, and `payout_requests.method` is whatever the member's request stored.
 */
export function fundsAutopostCodeSuggestions(
  f: FundsSuggestionFeatures,
): Record<FundsAutopostSource, FundsCodeSuggestion[]> {
  const pos = methodList(f.pos_payment_methods);
  const ecom = methodList(f.ecom_payment_methods);
  const pick = <T extends { code: string; label: string }>(rows: T[], keep: (m: T) => boolean) =>
    rows.filter(keep).map((m) => ({ code: m.code, label: m.label }));
  const manual = pick(ecom, (m) => m.settlement === 'manual');
  return {
    // Wallet tenders never post (the cash entered at top-up), so only the
    // drawer + exact-amount methods are candidates.
    pos_sale: pick(pos, (m) => m.tenderKind === 'cash' || m.tenderKind === 'exact'),
    gateway_payment: pick(ecom, (m) => m.settlement === 'gateway'),
    manual_payment: [
      ...manual,
      { code: OFFLINE_PAYMENT_METHOD_CODE, label: 'Offline payment (admin-recorded, no method)' },
    ],
    cod_collection: pick(ecom, (m) => m.settlement === 'cod_manual'),
    // Top-ups reuse the ecom MANUAL methods — there is no separate list.
    wallet_topup: manual,
    member_payout: pick(methodList(f.payout_deductions), () => true),
    merchant_payout: pick(methodList(f.merchant_payout_deductions), () => true),
    creator_payout: pick(methodList(f.creator_payout_deductions), () => true),
  };
}
